import React, { useCallback } from "react";
import type html2canvas from "html2canvas";

interface PrintDepthChartProps {
  depthChartRef: React.RefObject<HTMLDivElement>;
  selectedSide: string;
  selectedDepthChart: { name: string } | null;
}

export const usePrintDepthChart = (depthChartRef: React.RefObject<HTMLDivElement>, selectedSide: string, selectedDepthChart: { name: string } | null) => {
  return useCallback(async () => {
    if (!depthChartRef.current) return;

    try {
      const { default: html2canvas } = await import("html2canvas");

      const depthChartElement = depthChartRef.current;
      if (!depthChartElement) return;

      // Create a clone of the depth chart for printing
      const printElement = depthChartElement.cloneNode(true) as HTMLElement;
      
      // Add print preview class to the clone
      printElement.classList.add('print-preview');
      
      // Style the container for the clone
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.appendChild(printElement);
      document.body.appendChild(container);

      // Generate canvas from the clone with print styles
      const canvas = await html2canvas(printElement, {
        backgroundColor: "white",
        scale: 2,
        logging: false,
        useCORS: true,
        width: printElement.offsetWidth,
        height: printElement.offsetHeight,
      });

      // Clean up the temporary elements
      document.body.removeChild(container);

      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        alert("Please allow popups for this website to print the depth chart.");
        return;
      }

      const imageDataURL = canvas.toDataURL("image/png");

      // Get the styles from the PrintStyles.css
      const styleSheet = Array.from(document.styleSheets)
        .find(sheet => sheet.href?.includes('PrintStyles.css'));
      const printStyles = styleSheet ? Array.from(styleSheet.cssRules)
        .map(rule => rule.cssText).join('\n') : '';

      printWindow.document.write(`
        <html>
          <head>
            <title>${selectedDepthChart?.name} - ${selectedSide}</title>
            <style>
              @page {
                size: landscape;
              }
              body {
                margin: 0;
                padding: 10px;
                display: flex;
                flex-direction: column;
                align-items: center;
                background-color: white !important;
                color: black !important;
              }
              .print-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                margin-bottom: 10px;
                padding: 0 10px;
              }
              .print-title {
                font-size: 2rem;
                font-weight: bold;
              }
              .print-date {
                font-size: 1.2rem;
              }
              img {
                max-width: 100%;
                max-height: calc(100vh - 100px);
                width: auto;
                height: auto;
                background-color: white !important;
                object-fit: contain;
              }
              ${printStyles}
            </style>
          </head>
          <body class="print-preview">
            <div class="print-header">
              <div class="print-title">${selectedDepthChart?.name}</div>
              <div class="print-date">${new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}</div>
            </div>
            <img src="${imageDataURL}" alt="${selectedDepthChart?.name} - ${selectedSide}" />
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 300);
              };
            </script>
          </body>
        </html>
      `);

      printWindow.document.close();
    } catch (error) {
      console.error("Error generating print version:", error);
      alert("There was an error preparing the chart for printing.");
    }
  }, [depthChartRef, selectedSide, selectedDepthChart]);
};

const PrintDepthChart: React.FC<PrintDepthChartProps> = ({
  depthChartRef,
  selectedSide,
  selectedDepthChart,
}) => {
  const handlePrint = usePrintDepthChart(depthChartRef, selectedSide, selectedDepthChart);

  return (
    <button onClick={handlePrint}>Print</button>
  );
};

export default PrintDepthChart; 